import React from 'react';
import PropTypes from "prop-types";
import {useFormik} from "formik";

import GeoService from "../../../../../services/geo";
import BookingService from "../../../../../services/bookings";
import {DateFormatter} from "../../../../../services/date";
import CountryFlag from "../../../../../components/CountryFlag";
import {ResponsiveDialog} from "../../../../../components/ResponsiveDialog";
import LocalTouristTaxExclusionsSelect from "../../../../../components/LocalTouristTaxSelect";

import formGuestValidationSchema from "./validator";

import {createFilterOptions} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import {DatePicker} from "@mui/x-date-pickers";
import {useFeedbackTools} from "../../../../../components/FeedbackTools";


const {DOCUMENTS_TYPES} = BookingService.enums;

// Transform the form value to the format accepted by API
const getGuestData = g => {
    const nationalityCode = g.nationality?.code;
    const countryOfResidence = nationalityCode === "IT" ? g.countryOfResidence?.code : null;
    const placeOfResidence = countryOfResidence === "IT" ? g.placeOfResidence?.code : null;

    const countryOfBirthCode = g.countryOfBirth?.code;
    const placeOfBirth = countryOfBirthCode === "IT" ? g.placeOfBirth?.code : null;

    const hasDocument = !!g.documentType && !!g.documentNumber && !!g.countryOfIssue;

    return {
        name: g.name,
        surname: g.surname,
        sex: g.sex,
        nationality: nationalityCode,
        countryOfResidence: countryOfResidence,
        placeOfResidence: placeOfResidence,
        dateOfBirth: DateFormatter.onlyDateISOString(g.dateOfBirth),
        countryOfBirth: countryOfBirthCode,
        placeOfBirth: placeOfBirth,
        documentType: hasDocument ? g.documentType : null,
        documentNumber: hasDocument ? g.documentNumber : null,
        countryOfIssue: hasDocument ? g.countryOfIssue.code : null,
        localTouristTaxExclusionCode: g.localTouristTaxExclusionCode,
    }
}

const FormGuest = props => {
    const {guest, booking, isLoading, onSaveGuest, onClose} = props;
    const [residencyInputValue, setResidencyInputValue] = React.useState('');
    const [placeOfBirthInputValue, setPlaceOfBirthInputValue] = React.useState('');
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();

    const initialNationality = React.useMemo(() => {
        // Compute the nationality of last guest added
        let lastGuestNationality = null;
        if (booking.guests.length > 0) {
            const last = booking.guests[booking.guests.length - 1];
            lastGuestNationality = last.nationality;
        }

        return lastGuestNationality || booking.nationality;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const formik = useFormik({
        initialValues: {
            name: guest ? guest.name : '',
            surname: guest ? guest.surname : '',
            sex: guest ? guest.sex : null,
            nationality: guest ? guest.nationality : initialNationality,
            countryOfResidence: guest ? guest.countryOfResidence : initialNationality,
            placeOfResidence: guest ? guest.placeOfResidence : null,
            dateOfBirth: guest ? guest.dateOfBirth : null,
            countryOfBirth: guest ? guest.countryOfBirth : initialNationality,
            placeOfBirth: guest ? guest.placeOfBirth : null,
            documentType: guest ? guest.documentType || DOCUMENTS_TYPES[0].value : DOCUMENTS_TYPES[0].value,
            documentNumber: guest ? guest.documentNumber || '' : '',
            countryOfIssue: guest ? guest.countryOfIssue || initialNationality : initialNationality,
            localTouristTaxExclusionCode: guest?.localTouristTaxExclusionCode ?? ''
        },
        validationSchema: formGuestValidationSchema,
        validateOnMount: true,
        onSubmit: async values => {
            const data = getGuestData(values);
            try{
                await onSaveGuest({data});
                showSuccessNotification("operazione completata con successo");
            } catch (e) {
                console.error(e);
                showErrorNotification(e);
            }
        },
    });

    const title = !!guest ? "Modifica Ospite" : "Nuovo ospite";

    const disableSave = Object.keys(formik.errors).some(x => formik.touched[x]);

    const filterOptions = createFilterOptions({
        matchFrom: 'start'
    });

    return (
        <ResponsiveDialog title={title}
                          isOpen={true}
                          maxWidth="lg"
                          scroll="paper"
                          isLoading={isLoading}
                          showApply={true}
                          applyLabel="Salva"
                          enableApply={!disableSave}
                          onApply={formik.handleSubmit}
                          closeLabel="Cancella"
                          onClose={onClose}>
            <Grid container spacing={2} sx={{mt: 0.5}}>
                <Grid item xs={12} md={6}>
                    <TextField
                        disabled={guest?.isCheckedIn()}
                        required
                        label="Cognome"
                        fullWidth
                        name="surname"
                        onChange={formik.handleChange}
                        value={formik.values.surname}
                        error={formik.touched.surname && Boolean(formik.errors.surname)}
                        helperText={formik.touched.surname && formik.errors.surname}/>
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        disabled={guest?.isCheckedIn()}
                        required
                        label="Nome"
                        fullWidth
                        name="name"
                        onChange={formik.handleChange}
                        value={formik.values.name}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}/>
                </Grid>

                <Grid item xs={12}>
                    <FormControl disabled={guest?.isCheckedIn()}
                                 component="fieldset"
                                 error={formik.touched.sex && Boolean(formik.errors.sex)}>
                        <FormLabel component="legend">Sesso</FormLabel>
                        <RadioGroup row
                                    aria-label="gender"
                                    name="sex"
                                    value={formik.values.sex}
                                    onChange={formik.handleChange}>
                            <FormControlLabel value="M" control={<Radio/>} label="Maschio"/>
                            <FormControlLabel value="F" control={<Radio/>} label="Femmina"/>
                        </RadioGroup>
                        <FormHelperText>{formik.touched.sex && formik.errors.sex}</FormHelperText>
                    </FormControl>
                </Grid>
            </Grid>

            <Typography variant='h6' color={guest?.isCheckedIn() ? 'text.disabled' : 'primary'} sx={{mt: 2, mb: 2}}>
                Nazionalità e luogo di residenza
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        disabled={guest?.isCheckedIn()}
                        autoHighlight
                        options={GeoService.COUNTRIES}
                        filterOptions={filterOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={(event, value) => formik.setFieldValue("nationality", value)}
                        value={formik.values.nationality}
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                sx={{fontSize: 15, '& > img': {mr: 1}}}
                                {...props}>
                                <CountryFlag code={option.code}/>
                                {option.label}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Nazionalità"
                                fullWidth
                                error={formik.touched.nationality && Boolean(formik.errors.nationality)}
                                helperText={formik.touched.nationality && formik.errors.nationality}
                            />
                        )}
                    />
                </Grid>

                {
                    formik.values.nationality?.code === "IT" &&
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            disabled={guest?.isCheckedIn()}
                            autoHighlight
                            options={GeoService.COUNTRIES}
                            filterOptions={filterOptions}
                            getOptionLabel={(option) => option.label}
                            onChange={(event, value) => formik.setFieldValue("countryOfResidence", value)}
                            value={formik.values.countryOfResidence}
                            renderOption={(props, option) => (
                                <Box
                                    component="li"
                                    sx={{fontSize: 15, '& > img': {mr: 1}}}
                                    {...props}>
                                    <CountryFlag code={option.code}/>
                                    {option.label}
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Nazione di residenza"
                                    fullWidth
                                    error={formik.touched.countryOfResidence && Boolean(formik.errors.countryOfResidence)}
                                    helperText={formik.touched.countryOfResidence && formik.errors.countryOfResidence}
                                />
                            )}
                        />
                    </Grid>
                }
                {
                    formik.values.nationality?.code === "IT" &&
                    formik.values.countryOfResidence?.code === "IT" &&
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            autoHighlight
                            disabled={!formik.values.countryOfResidence || guest?.isCheckedIn()}
                            options={residencyInputValue.length > 0 ? GeoService.CITIES : []}
                            noOptionsText={residencyInputValue.length > 0 ? "Nessuna opzione" : "Cerca comune"}
                            filterOptions={filterOptions}
                            getOptionLabel={(option) => `${option.name} (${option.districtInitials})`}
                            onChange={(event, value) => formik.setFieldValue("placeOfResidence", value)}
                            value={formik.values.placeOfResidence}
                            inputValue={residencyInputValue}
                            onInputChange={(event, newInputValue) => {
                                setResidencyInputValue(newInputValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Comune di residenza"
                                    fullWidth
                                    error={formik.touched.placeOfResidence && Boolean(formik.errors.placeOfResidence)}
                                    helperText={formik.touched.placeOfResidence && formik.errors.placeOfResidence}
                                />
                            )}
                        />
                    </Grid>
                }
            </Grid>

            <Typography variant='h6' color={guest?.isCheckedIn() ? 'text.disabled' : 'primary'} sx={{mt: 3, mb: 2}}>
                Data e luogo di nascita
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <DatePicker disabled={guest?.isCheckedIn()}
                                openTo="year"
                                views={['year', 'month', 'day']}
                                label="Data di nascita"
                                disableHighlightToday
                                closeOnSelect
                                maxDate={new Date()}
                                onChange={date => formik.setFieldValue("dateOfBirth", date)}
                                value={formik.values.dateOfBirth}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: Boolean(formik.touched.dateOfBirth) && Boolean(formik.errors.dateOfBirth),
                                        helperText: formik.touched.dateOfBirth && formik.errors.dateOfBirth
                                    }
                                }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Autocomplete
                        disabled={guest?.isCheckedIn()}
                        autoHighlight
                        options={GeoService.COUNTRIES}
                        filterOptions={filterOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={(event, value) => formik.setFieldValue("countryOfBirth", value)}
                        value={formik.values.countryOfBirth}
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                sx={{fontSize: 15, '& > img': {mr: 1}}}
                                {...props}>
                                <CountryFlag code={option.code}/>
                                {option.label}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Nazione di nascita"
                                fullWidth
                                error={formik.touched.countryOfBirth && Boolean(formik.errors.countryOfBirth)}
                                helperText={formik.touched.countryOfBirth && formik.errors.countryOfBirth}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    {
                        formik.values.countryOfBirth?.code === "IT" &&
                        <Autocomplete
                            autoHighlight
                            disabled={!formik.values.countryOfBirth || guest?.isCheckedIn()}
                            options={placeOfBirthInputValue.length > 0 ? GeoService.CITIES : []}
                            noOptionsText={placeOfBirthInputValue.length > 0 ? "Nessuna opzione" : "Cerca comune"}
                            filterOptions={filterOptions}
                            getOptionLabel={(option) => `${option.name} (${option.districtInitials})`}
                            onChange={(event, value) => formik.setFieldValue("placeOfBirth", value)}
                            value={formik.values.placeOfBirth}
                            inputValue={placeOfBirthInputValue}
                            onInputChange={(event, newInputValue) => {
                                setPlaceOfBirthInputValue(newInputValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Comune di nascita"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: 'new-password', // disable autocomplete and autofill
                                    }}
                                    fullWidth
                                    error={formik.touched.placeOfBirth && Boolean(formik.errors.placeOfBirth)}
                                    helperText={formik.touched.placeOfBirth && formik.errors.placeOfBirth}/>
                            )}
                        />
                    }
                </Grid>
            </Grid>

            <Typography variant='h6' color='primary' sx={{mt: 3, mb: 2}}>
                Documento d'identità
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <TextField
                        select
                        label="Tipo documento"
                        fullWidth
                        name="documentType"
                        value={formik.values.documentType}
                        onChange={formik.handleChange}
                        error={formik.touched.documentType && Boolean(formik.errors.documentType)}
                        helperText={formik.touched.documentType && formik.errors.documentType}>
                        {DOCUMENTS_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Autocomplete
                        autoHighlight
                        options={GeoService.COUNTRIES}
                        filterOptions={filterOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={(event, value) => formik.setFieldValue("countryOfIssue", value)}
                        value={formik.values.countryOfIssue}
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                sx={{fontSize: 15, '& > img': {mr: 1}}}
                                {...props}>
                                <CountryFlag code={option.code}/>
                                {option.label}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label="Nazione di rilascio"
                                       fullWidth
                                       error={formik.touched.countryOfIssue && Boolean(formik.errors.countryOfIssue)}
                                       helperText={formik.touched.countryOfIssue && formik.errors.countryOfIssue}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        label="Numero documento"
                        fullWidth
                        name="documentNumber"
                        value={formik.values.documentNumber}
                        onChange={formik.handleChange}
                        error={formik.touched.documentNumber && Boolean(formik.errors.documentNumber)}
                        helperText={formik.touched.documentNumber && formik.errors.documentNumber}/>
                </Grid>
            </Grid>

            <Typography variant='h6' color='primary' sx={{mt: 3, mb: 2}}>
                Esenzione tassa di soggiorno
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <LocalTouristTaxExclusionsSelect formik={formik}
                                                     name='localTouristTaxExclusionCode'
                                                     label='Codice esenzione tassa di soggiorno (abit)'
                                                     fullWidth/>
                </Grid>
            </Grid>
        </ResponsiveDialog>
    );
};

FormGuest.propTypes = {
    guest: PropTypes.object,
    booking: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    onSaveGuest: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
}

export default FormGuest;